import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import logo from "./logo.jpg";
import appPreview from "./sc.png";
import { CheckCircle, ArrowRight, Shield, Download, ChevronDown, Check } from "lucide-react";
import { insertLead } from "./lib/supabase";
import LeadsAdmin from "./components/LeadsAdmin";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";

const MinimalLanding = () => {
  const APP_STORE_URL = "https://apps.apple.com/us/app/supa-kid/id6757116935";

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfKids: '1'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Send data to Supabase
      const result = await insertLead(formData);
      
      if (result.success) {
        setIsSubmitted(true);
        console.log('Lead saved successfully:', result.data);
      } else {
        setSubmitError('Failed to save your information. Please try again.');
        console.error('Error saving lead:', result.error);
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again.');
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center max-w-md w-full shadow-2xl border border-white/10">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">You're on the list.</h2>
          <p className="text-white/80 mb-6">
            Android is coming soon. We'll email you when SupaKid is ready on Google Play.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-white/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download on the App Store</span>
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              <span>Back</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-950 to-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-950/70 backdrop-blur border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={logo} alt="SupaKid Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold tracking-tight">SupaKid</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 font-semibold hover:bg-white/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>App Store</span>
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              <span>See how it works</span>
              <ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-10 sm:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-sm text-white/80 mb-6">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                <span>iOS is live • Android coming soon</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Curated videos kids love—with{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300">
                  guardrails
                </span>{" "}
                you control.
              </h1>
              <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl">
                Age-appropriate learning and entertainment in one place. Pick an age level—we keep content aligned with it.
              </p>

              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-white/90 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download on the App Store</span>
                </a>
                <a
                  href="#how-it-works"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/15 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  <span>See how it works</span>
                  <ChevronDown className="w-4 h-4" />
                </a>
              </div>

              <div className="mt-4 text-sm text-white/60">iOS available now • Android coming soon</div>
            </div>

            <div className="relative">
              {/* App-matching glow */}
              <div className="absolute -inset-8 bg-gradient-to-b from-fuchsia-500/25 via-purple-500/25 to-sky-500/25 blur-3xl rounded-[2rem]" />
              <div className="relative mx-auto w-full max-w-sm sm:max-w-md lg:max-w-sm xl:max-w-md rounded-3xl overflow-hidden shadow-2xl border border-white/10">
                {/* App-like gradient backdrop behind screenshot */}
                <div className="absolute inset-0 bg-gradient-to-b from-fuchsia-500/30 via-purple-500/25 to-sky-500/25" />
                <div className="absolute inset-0 bg-black/20" />
                <img
                  src={appPreview}
                  alt="SupaKid app preview"
                  className="relative w-full h-auto"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Problem -> Solution */}
          <section className="mt-12 sm:mt-16 bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8">
            <div className="max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold">Less chaos. More intention.</h2>
              <p className="mt-3 text-white/80 leading-relaxed">
                Hand-picked videos by topic, with profiles, PINs, and time limits—so screen time doesn’t turn into endless scrolling.
              </p>
            </div>
          </section>

          {/* What parents get */}
          <section className="mt-12 sm:mt-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What parents get</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "Child profiles", desc: "Separate spaces for each kid." },
                { title: "Age levels", desc: "Content tuned to their stage." },
                { title: "Curated library", desc: "Browse by category—no open wandering." },
                { title: "PIN protection", desc: "Lock settings behind a parent code." },
                { title: "Screen time limits", desc: "Set minutes per session/day." },
              ].map((f) => (
                <div key={f.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-8 h-8 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{f.title}</h3>
                      <p className="text-white/75 mt-1">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How it works */}
          <section id="how-it-works" className="mt-12 sm:mt-16 scroll-mt-28">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">How it works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: "01", title: "Set up", desc: "Create an account and parent PIN." },
                { step: "02", title: "Choose levels", desc: "Add profiles and age bands." },
                { step: "03", title: "Press play", desc: "Browse categories with limits enabled." },
              ].map((s) => (
                <div key={s.step} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="text-sm text-white/60 font-semibold">{s.step}</div>
                  <div className="mt-2 font-bold text-xl">{s.title}</div>
                  <p className="mt-2 text-white/75">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Screenshots gallery */}
          <section className="mt-12 sm:mt-16">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="text-2xl md:text-3xl font-bold">Screenshots</h2>
              <span className="text-sm text-white/60">From the iOS build</span>
            </div>
            <div className="sm:hidden -mx-4 px-4 flex gap-4 overflow-x-auto snap-x snap-mandatory">
              {[
                "/screenshots/supakid-01.png",
                "/screenshots/supakid-02.png",
                "/screenshots/supakid-03.png",
                "/screenshots/supakid-04.png",
                "/screenshots/supakid-05.png",
              ].map((src) => (
                <a
                  key={src}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="snap-start shrink-0 w-72 bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                >
                  <img
                    src={src}
                    alt="SupaKid screenshot"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>

            <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "/screenshots/supakid-01.png",
                "/screenshots/supakid-02.png",
                "/screenshots/supakid-03.png",
                "/screenshots/supakid-04.png",
                "/screenshots/supakid-05.png",
              ].map((src) => (
                <a
                  key={src}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 transition-colors"
                >
                  <img
                    src={src}
                    alt="SupaKid screenshot"
                    className="w-full h-auto group-hover:scale-[1.01] transition-transform duration-300"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </section>

          {/* Android waitlist */}
          <section className="mt-12 sm:mt-16 bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">Android is coming soon</h2>
                <p className="mt-2 text-white/80 max-w-2xl">
                  Want a heads-up when SupaKid launches on Android? Join the notification list.
                </p>
              </div>
              <div className="lg:min-w-[420px]">
                <form onSubmit={handleSubmit} className="space-y-3">
                  {submitError && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-200 px-4 py-3 rounded-xl">
                      <div className="flex items-center">
                        <div className="text-red-300 mr-2">⚠️</div>
                        <span className="font-medium text-sm">{submitError}</span>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                      placeholder="Full Name *"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                      placeholder="Phone Number *"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all sm:col-span-2"
                      placeholder="Email Address *"
                    />
                    <select
                      name="numberOfKids"
                      value={formData.numberOfKids}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all sm:col-span-2"
                    >
                      <option value="1">1 Child</option>
                      <option value="2">2 Children</option>
                      <option value="3">3 Children</option>
                      <option value="4">4 Children</option>
                      <option value="5+">5+ Children</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 text-gray-900 font-semibold hover:opacity-95 transition-opacity disabled:opacity-60"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                        <span>Submitting…</span>
                      </>
                    ) : (
                      <>
                        <span>Notify me for Android</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-white/60">
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </section>

          {/* Subscription */}          
          <section className="mt-12 sm:mt-16 bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Subscription</h2>
            <p className="text-white/80 leading-relaxed max-w-4xl">
              Start with a <span className="font-semibold text-white">free first month</span> trial on eligible plans, then continue with one of the options below. Cancel anytime in your Apple ID settings.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
              <div className="bg-black/30 border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-white/60 font-semibold">Monthly</div>
                <div className="mt-1 text-3xl font-bold">$10<span className="text-base font-semibold text-white/70">/month</span></div>
                <div className="mt-2 text-sm text-white/70">Free first month trial on eligible subscriptions.</div>
              </div>
              <div className="bg-black/30 border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-white/60 font-semibold">Annual</div>
                <div className="mt-1 text-3xl font-bold">$60<span className="text-base font-semibold text-white/70">/year</span></div>
                <div className="mt-2 text-sm text-white/70">Billed annually. Free first month trial on eligible subscriptions.</div>
              </div>
            </div>
            <p className="mt-4 text-xs text-white/60 max-w-4xl">
              Pricing and trial availability may vary by region and are shown in the App Store before purchase.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10">
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col lg:flex-row justify-between gap-8">
            <div>
              <div className="flex items-center space-x-3">
                <img src={logo} alt="SupaKid Logo" className="w-10 h-10 rounded-xl" />
                <span className="text-xl font-bold">SupaKid</span>
              </div>
              <p className="mt-3 text-sm text-white/70 max-w-lg">
                SupaKid is a third-party app; video content is provided by external creators/platforms. Parents remain responsible for supervising viewing.
              </p>
              <p className="mt-3 text-sm text-white/70">
                Support:{" "}
                <a
                  href="mailto:admin@SupaKidapp.com"
                  className="text-white/90 hover:text-white underline"
                >
                  admin@SupaKidapp.com
                </a>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-10">
              <div>
                <div className="text-sm font-semibold text-white/80 mb-3">Download</div>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-gray-900 font-semibold hover:bg-white/90 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>App Store</span>
                </a>
                <div className="mt-2 text-xs text-white/60">Android coming soon</div>
              </div>

              <div>
                <div className="text-sm font-semibold text-white/80 mb-3">Legal</div>
                <div className="flex flex-col gap-2 text-sm text-white/70">
                  <Link to="/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Use
                  </Link>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-white/80 mb-3">Admin</div>
                <Link
                  to="/leads"
                  className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 text-xs text-white/50">
            © {new Date().getFullYear()} SupaKid LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MinimalLanding />} />
        <Route path="/leads" element={<LeadsAdmin />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
      </Routes>
    </Router>
  );
};

export default App;
